import * as React from 'react'
import { Observable } from 'rxjs'
import { useObservableCallback } from './rx-hooks'

/**
 * Props for the hook.
 */
export interface UseFocusProps<F extends (...args: any[]) => any> {
  onFocus?: F
  onBlur?: F
}

/**
 * Result for the hook. First item is props to spread to target element, 2nd is
 * whether the element has focus.
 */
export type UseFocusResult<F extends (...args: any[]) => any> = [
  { onFocus: F; onBlur: F },
  boolean,
]

/**
 * Focus tracking.
 *
 * @param props
 */
export function useFocusTracking<F extends (...args: any[]) => any>(
  props?: UseFocusProps<F>,
): UseFocusResult<F> {
  const [focused, setFocused] = React.useState(false)
  const onFocus: F = React.useCallback(
    (...args: any[]) => {
      setFocused(true)
      return props?.onFocus?.(...args)
    },
    [props?.onFocus],
  ) as F
  const onBlur = React.useCallback(
    (...args: any[]) => {
      setFocused(false)
      return props?.onBlur?.(...args)
    },
    [props?.onBlur],
  ) as F
  const focusProps = React.useMemo(
    () => ({ onFocus, onBlur }),
    [onFocus, onBlur],
  )

  return [focusProps, focused]
}

/**
 * Calls the focus function or `.focus()` on the ref when the `focus$`
 * observable emits.
 *
 * @param focus$ Observable that emits when we need to focus. Should be a
 *  stable reference.
 * @param focusFunctionOrRef This should be stable, don't change from a ref
 *  to a function between renders or vice versa.
 */
export function useObservableFocus(
  focus$: Observable<unknown> | undefined,
  focusFunctionOrRef:
    | React.RefObject<{ focus: () => unknown }>
    | (() => unknown),
) {
  useObservableCallback(focus$, () => {
    if (typeof focusFunctionOrRef === 'function') {
      focusFunctionOrRef()
    } else {
      focusFunctionOrRef.current?.focus()
    }
  }, [focus$, focusFunctionOrRef])
}
